import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { getContentPath } from '../../common/ContentPaths';
import { useLoadedDocument } from './useLoadedDocument';
import { useOptionalBarRaiserEvaluationService } from '../../contexts/BarRaiserEvaluationServiceContext';

/**
 * Redirects a bar raiser to the published version once their publish decision
 * succeeds. Does nothing unless the just-published content's draft is being viewed.
 */
export function usePublishSuccessRedirect(): void {
  const service = useOptionalBarRaiserEvaluationService();
  const doc = useLoadedDocument();
  const history = useHistory();

  // Expose the latest document to the listener without re-subscribing on every document change.
  const docRef = useRef(doc);
  docRef.current = doc;

  // The content we've already redirected for, so a still-set marker doesn't redirect twice.
  const redirectedForRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!service) return;

    // When the service marks a content as just published while its draft is open, redirect to the published version.
    return service.on('changed', () => {
      const publishedContentId = service.lastPublishedContentId;

      // When the marker is cleared (dismissed), re-arm for the next publish.
      if (publishedContentId === undefined) {
        redirectedForRef.current = undefined;
        return;
      }

      // Redirect at most once per publish.
      if (redirectedForRef.current === publishedContentId) return;

      // Only redirect from the just-published content's own draft.
      const current = docRef.current;
      if (!current || current.kind !== 'draft' || current.contentId !== publishedContentId) return;

      redirectedForRef.current = publishedContentId;
      history.push(getContentPath({ contentId: publishedContentId, mode: 'publication' }));
    });
  }, [service, history]);
}
